import { createFileRoute } from "@tanstack/react-router";
import { RouteError, RouteNotFound } from "@/components/route-error";
import { useQuery } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { getPeople } from "@/lib/relationships.functions";
import { useEffect, useMemo, useRef, useState } from "react";
import { Search, X } from "lucide-react";
import { PersonRow } from "@/components/person-row";
import { OrbitMap } from "@/components/orbit-map";
import { OrbitSheet } from "@/components/orbit-sheet";
import { AccountChip } from "@/components/account-chip";
import { useIsMobile } from "@/hooks/use-mobile";
import { getQueueSnapshot, subscribeQueue, type QueueSnapshot } from "@/lib/enrichment-queue";

export const Route = createFileRoute("/_authenticated/people")({
  errorComponent: RouteError,
  notFoundComponent: RouteNotFound,
  component: People,
  validateSearch: (s: Record<string, unknown>): { q?: string; group?: string } => ({
    ...(typeof s.q === "string" && s.q ? { q: s.q } : {}),
    ...(typeof s.group === "string" && s.group ? { group: s.group } : {}),
  }),
});

const RAIL_THRESHOLD = 30;

/** Progress line for the background discovery queue after a multi-person add. */
function BuildingBanner() {
  const [snap, setSnap] = useState<QueueSnapshot>(() => getQueueSnapshot());
  useEffect(() => subscribeQueue(setSnap), []);
  if (!snap.running || snap.total === 0) return null;
  return (
    <div className="glass-soft mb-3 flex items-center gap-2 rounded-2xl px-4 py-2.5 text-[13px] text-muted-foreground">
      <span className="relative flex h-2 w-2 shrink-0">
        <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-primary/60" />
        <span className="relative inline-flex h-2 w-2 rounded-full bg-primary" />
      </span>
      Building {snap.total} {snap.total === 1 ? "profile" : "profiles"} — {snap.done} done
    </div>
  );
}

function lastNameKey(name: string): string {
  const parts = String(name ?? "").trim().split(/\s+/);
  const last = parts.length > 1 ? parts[parts.length - 1] : parts[0] ?? "";
  const letter = (last[0] ?? "#").toUpperCase();
  return /[A-Z]/.test(letter) ? letter : "#";
}

function People() {
  const fetchPeople = useServerFn(getPeople);
  const { data } = useQuery({ queryKey: ["people"], queryFn: () => fetchPeople() });
  const search = Route.useSearch();
  const [q, setQ] = useState(search.q ?? "");
  const [activeCat, setActiveCat] = useState<string | null>(search.group ?? null);
  const [sheetSnap, setSheetSnap] = useState(0.19);
  // Measure from the stage's top to the true viewport bottom so the map and the
  // pinned drawer share the same bottom edge (no dead band under the drawer).
  const stageRef = useRef<HTMLDivElement | null>(null);
  const [stageH, setStageH] = useState(0);
  useEffect(() => {
    const measure = () => {
      const el = stageRef.current;
      if (!el) return;
      const vh = window.visualViewport?.height ?? window.innerHeight;
      setStageH(Math.max(240, Math.round(vh - el.getBoundingClientRect().top)));
    };
    measure();
    const id = window.setTimeout(measure, 120);
    window.addEventListener("resize", measure);
    window.visualViewport?.addEventListener("resize", measure);
    return () => {
      window.clearTimeout(id);
      window.removeEventListener("resize", measure);
      window.visualViewport?.removeEventListener("resize", measure);
    };
  }, []);
  const isMobile = useIsMobile();

  useEffect(() => { setQ(search.q ?? ""); }, [search.q]);
  useEffect(() => { setActiveCat(search.group ?? null); }, [search.group]);

  const letterRefs = useRef<Record<string, HTMLDivElement | null>>({});

  const allContacts = data?.contacts ?? [];
  const categories = data?.categories ?? [];

  const filtered = useMemo(() => {
    return allContacts.filter((c: any) => {
      if (activeCat) {
        const ids: string[] = c.group_ids ?? (c.category_id ? [c.category_id] : []);
        if (!ids.includes(activeCat)) return false;
      }
      if (q) {
        const haystack = `${c.name ?? ""} ${c.company ?? ""} ${c.title ?? ""}`.toLowerCase();
        if (!haystack.includes(q.toLowerCase())) return false;
      }
      return true;
    });
  }, [allContacts, q, activeCat]);

  const { alpha, alphaLetters } = useMemo(() => {
    const rest = [...filtered].sort((a: any, b: any) => {
      const byLetter = lastNameKey(a.name).localeCompare(lastNameKey(b.name));
      return byLetter !== 0 ? byLetter : String(a.name).localeCompare(String(b.name));
    });
    const alpha: Record<string, any[]> = {};
    for (const c of rest) (alpha[lastNameKey(c.name)] ||= []).push(c);
    return { alpha, alphaLetters: Object.keys(alpha).sort() };
  }, [filtered]);

  const totalCount = allContacts.length;
  const showRail = totalCount > RAIL_THRESHOLD && alphaLetters.length > 1;

  const toggleCat = (id: string) => setActiveCat((curr) => (curr === id ? null : id));

  const scrollTo = (el: HTMLElement | null) => {
    if (!el) return;
    el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const searchField = (
    <div className="flex items-center gap-2">
      <div className="glass min-w-0 flex-1 rounded-full h-12 px-4 flex items-center gap-2.5 shadow-lg">
      <Search className="h-5 w-5 text-muted-foreground shrink-0" />
      <input
        value={q}
        onChange={(e) => setQ(e.target.value)}
        placeholder="Search name, company, title"
        className="w-full min-w-0 bg-transparent text-[15px] outline-none placeholder:text-muted-foreground"
      />
      {q && (
        <button
          type="button"
          aria-label="Clear search"
          onClick={() => setQ("")}
          className="shrink-0 text-muted-foreground hover:text-foreground"
        >
          <X className="h-5 w-5" />
        </button>
      )}
      </div>
      <div className="shrink-0"><AccountChip /></div>
    </div>
  );

  const pillRow =
    categories.length > 0 ? (
      <div className="mt-2 -mx-4 px-4 flex items-center gap-2 overflow-x-auto no-scrollbar pb-0.5">
        <FilterPill active={activeCat === null} onClick={() => setActiveCat(null)}>
          All
        </FilterPill>
        {categories.map((c: any) => {
          const count = allContacts.filter((p: any) => {
            const ids: string[] = p.group_ids ?? (p.category_id ? [p.category_id] : []);
            return ids.includes(c.id);
          }).length;
          return (
            <FilterPill key={c.id} active={activeCat === c.id} onClick={() => toggleCat(c.id)}>
              <span className={`whitespace-nowrap ${count === 0 && activeCat !== c.id ? "opacity-50" : ""}`}>
                {c.name} <span className="font-normal opacity-60">{count}</span>
              </span>
            </FilterPill>
          );
        })}
        {activeCat && (
          <button
            onClick={() => setActiveCat(null)}
            className="shrink-0 whitespace-nowrap px-2 text-[13px] text-muted-foreground hover:text-foreground"
          >
            Clear
          </button>
        )}
      </div>
    ) : null;

  // At the drawer's resting snap only the first card peeks out, so the A–Z
  // letter heading would eat that row.
  const hideLetters = isMobile && sheetSnap <= 0.19;

  const directory = (
    <>
      <BuildingBanner />
      {filtered.length === 0 && (
        <div className="glass-soft rounded-2xl p-8 text-center text-[15px] text-muted-foreground">
          {activeCat
            ? `No one in ${categories.find((c: any) => c.id === activeCat)?.name ?? "this group"} yet — open a person and use the group pencil to add them.`
            : "No one matches these filters."}
        </div>
      )}

      {alphaLetters.length > 0 && (
        <div className={hideLetters ? "space-y-3" : "space-y-6"}>
          {alphaLetters.map((letter) => (
            <div
              key={letter}
              ref={(el) => { letterRefs.current[letter] = el; }}
              className="scroll-mt-24"
            >
              {!hideLetters && (
                <div className="mb-2 inline-flex items-center text-[13px] font-semibold text-muted-foreground">
                  {letter}
                </div>
              )}
              <ul className="grid gap-3 min-w-0 2xl:grid-cols-2">
                {alpha[letter].map((c: any) => (
                  <li key={c.id} className="min-w-0"><PersonRow contact={c} /></li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      )}
    </>
  );

  const activeCategoryName = activeCat
    ? categories.find((c: any) => c.id === activeCat)?.name
    : null;
  const activeGroupCount = activeCat
    ? allContacts.filter((p: any) => {
        const ids: string[] = p.group_ids ?? (p.category_id ? [p.category_id] : []);
        return ids.includes(activeCat);
      }).length
    : 0;
  const labelCount = q.trim() ? filtered.length : activeGroupCount;
  const restingCount = q.trim() ? filtered.length : allContacts.length;
  const sheetLabel = activeCategoryName
    ? `${activeCategoryName} - ${labelCount} ${labelCount === 1 ? "Person" : "People"}`
    : `${restingCount} ${restingCount === 1 ? "person" : "people"}`;

  if (isMobile) {
    const searchSignal =
      q.trim().length > 0 && filtered.length > 0 ? `q:${q.trim()}` : null;
    const overlayHidden = sheetSnap >= 0.6;
    return (
      <div
        ref={stageRef}
        className="relative -mx-4 -mb-24 overflow-hidden"
        style={{ height: stageH ? `${stageH}px` : "calc(100dvh - 4.75rem - env(safe-area-inset-top))" }}
      >
        <OrbitMap
          fill
          bottomInset={0.19}
          topInset={0.07}
          categories={categories}
          contacts={allContacts}
          activeCategory={activeCat}
          onSelectCategory={(id) => { setActiveCat(id ?? null); if (id) setQ(""); }}
        />

        <div
          className="absolute inset-x-0 top-0 z-30 px-4 pb-2 pt-1 transition-all duration-300"
          style={{
            opacity: overlayHidden ? 0 : 1,
            transform: overlayHidden ? "translateY(-12px)" : "none",
            pointerEvents: overlayHidden ? "none" : "auto",
          }}
        >
          <div>{searchField}</div>
          <div>{pillRow}</div>
        </div>

        <OrbitSheet
          openSignal={activeCat ?? searchSignal}
          label={sheetLabel}
          onSnapChange={setSheetSnap}
        >
          {directory}
        </OrbitSheet>
      </div>
    );
  }

  return (
    <div className="space-y-4 pb-4">
      <div className="sticky top-0 z-20 -mx-4 px-4 pb-2 pt-2 backdrop-blur-md bg-background/80">
        <div className="flex items-center gap-2">
        <div className="glass-soft min-w-0 flex-1 rounded-full h-12 px-4 flex items-center gap-2.5">
          <Search className="h-5 w-5 text-muted-foreground shrink-0" />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search name, company, title"
            className="w-full min-w-0 bg-transparent text-[15px] outline-none placeholder:text-muted-foreground"
          />
          {q && (
            <button
              type="button"
              aria-label="Clear search"
              onClick={() => setQ("")}
              className="shrink-0 text-muted-foreground hover:text-foreground"
            >
              <X className="h-5 w-5" />
            </button>
          )}
        </div>
        <div className="shrink-0"><AccountChip /></div>
        </div>

        {categories.length > 0 && (
          <div className="mt-3 -mx-4 px-4 flex items-center gap-2 overflow-x-auto no-scrollbar pb-0.5">
            <FilterPill active={activeCat === null} onClick={() => setActiveCat(null)}>
              All
            </FilterPill>
            {categories.map((c: any) => {
              const count = allContacts.filter((p: any) => {
                const ids: string[] = p.group_ids ?? (p.category_id ? [p.category_id] : []);
                return ids.includes(c.id);
              }).length;
              return (
                <FilterPill key={c.id} active={activeCat === c.id} onClick={() => toggleCat(c.id)}>
                  <span className={`whitespace-nowrap ${count === 0 && activeCat !== c.id ? "opacity-50" : ""}`}>
                    {c.name} <span className="font-normal opacity-60">{count}</span>
                  </span>
                </FilterPill>
              );
            })}
            {activeCat && (
              <button
                onClick={() => setActiveCat(null)}
                className="shrink-0 whitespace-nowrap px-2 text-[13px] text-muted-foreground hover:text-foreground"
              >
                Clear
              </button>
            )}
          </div>
        )}
      </div>

      <div className="-mx-4 md:mx-0">
        <OrbitMap
          categories={categories}
          contacts={allContacts}
          activeCategory={activeCat}
          onSelectCategory={(id) => { setActiveCat(id ?? null); if (id) setQ(""); }}
        />
      </div>

      <div className="relative">
        <BuildingBanner />
        {filtered.length === 0 && (
          <div className="glass-soft rounded-2xl p-8 text-center text-[15px] text-muted-foreground">
            {activeCat
              ? `No one in ${categories.find((c: any) => c.id === activeCat)?.name ?? "this group"} yet — open a person and use the group pencil to add them.`
              : "No one matches these filters."}
          </div>
        )}

        {alphaLetters.length > 0 && (
          <section className="space-y-3">
            <div className="space-y-6">
              {alphaLetters.map((letter) => (
                <div
                  key={letter}
                  ref={(el) => { letterRefs.current[letter] = el; }}
                  className="scroll-mt-24"
                >
                  <div className="sticky top-[68px] z-10 mb-2 inline-flex items-center text-[13px] font-semibold text-muted-foreground">
                    {letter}
                  </div>
                  <ul className="grid gap-3 min-w-0 2xl:grid-cols-2">
                    {alpha[letter].map((c: any) => (
                      <li key={c.id} className="min-w-0"><PersonRow contact={c} /></li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </section>
        )}
      </div>

      {showRail && (
        <nav
          aria-label="Jump to letter"
          className="fixed right-1 top-1/2 -translate-y-1/2 z-30 hidden sm:flex flex-col gap-0.5 px-1 py-2 rounded-full glass-soft text-[11px] font-medium text-muted-foreground"
        >
          {alphaLetters.map((letter) => (
            <button
              key={letter}
              onClick={() => scrollTo(letterRefs.current[letter])}
              className="px-1.5 py-0.5 hover:text-primary"
            >
              {letter}
            </button>
          ))}
        </nav>
      )}
    </div>
  );
}

function FilterPill({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`inline-flex h-8 shrink-0 items-center whitespace-nowrap rounded-full px-3 text-[13px] font-medium leading-none transition-colors ${
        active
          ? "bg-primary text-primary-foreground"
          : "glass-soft text-foreground/80 hover:text-foreground"
      }`}
    >
      {children}
    </button>
  );
}
